// "Reach out to join" — the sheet a viewer uses to ask to join a kiki. They
// write an optional note and can opt into sharing their Instagram handle with
// the host (and, once accepted, the group). First name + photo are always
// shared.

import { useEffect, useMemo, useState } from "react";
import { Instagram, Loader2, Send } from "lucide-react";
import { SheetModal } from "./SheetModal";
import {
  useApplyToBuddy,
  type BuddyApplicationSharedProfile,
} from "../lib/api/useEventBuddies";
import { useProfile } from "../lib/api/useProfile";
import { useToast } from "./ux/Toast";
import tracking from "../lib/tracking";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  requestId: string;
  posterName: string;
}

export function ReachOutModal({ isOpen, onClose, slug, requestId, posterName }: Props) {
  const apply = useApplyToBuddy(slug);
  const toast = useToast();
  const { data: profileData } = useProfile();
  const profile = profileData?.success ? profileData.data?.profile : null;

  const profileInstagram = useMemo(() => {
    const ig = (profile as Record<string, unknown> | null)?.instagram;
    return typeof ig === "string" ? ig.trim().replace(/^@/, "") : "";
  }, [profile]);

  const [note, setNote] = useState("");
  const [shareIg, setShareIg] = useState(false);
  const [igHandle, setIgHandle] = useState("");

  // Seed each time the sheet opens.
  useEffect(() => {
    if (!isOpen) return;
    setNote("");
    setShareIg(!!profileInstagram);
    setIgHandle(profileInstagram);
  }, [isOpen, profileInstagram]);

  const handleSubmit = async () => {
    const sharedProfile: BuddyApplicationSharedProfile = {};
    const ig = shareIg ? igHandle.trim().replace(/^@/, "") : "";
    if (ig) sharedProfile.instagram = ig;
    const sp = Object.keys(sharedProfile).length > 0 ? sharedProfile : undefined;

    try {
      const res = await apply.mutateAsync({
        id: requestId,
        note: note.trim() || undefined,
        sharedProfile: sp,
      });
      if (res.success) {
        tracking.kikiReachOutSubmitted(requestId, !!note.trim());
        toast.success("Sent!");
        onClose();
      } else {
        toast.error(res.error || "Couldn't send");
      }
    } catch (err) {
      toast.error((err as Error).message || "Couldn't send");
    }
  };

  return (
    <SheetModal isOpen={isOpen} onClose={onClose} className="sm:max-w-lg">
      <div className="px-5 pt-5 pb-4">
        <h2 className="text-lg font-semibold">Reach out to join</h2>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
          {posterName} sees your first name and photo. Add a note to introduce yourself.
        </p>
      </div>

      <div className="px-5 pb-2 space-y-4">
        {/* Note — the main thing */}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder={`Say hi or why you'd like to go with ${posterName}…`}
          className="input resize-none w-full"
        />

        {/* Instagram — a small opt-in setting, not a section */}
        <div className="rounded-xl border border-black/10 dark:border-white/10 px-3.5 py-3">
          <button
            type="button"
            role="switch"
            aria-checked={shareIg}
            onClick={() => setShareIg((v) => !v)}
            className="flex w-full items-center gap-3 text-left"
          >
            <Instagram className="w-4 h-4 text-neutral-500 shrink-0" />
            <span className="text-sm flex-1">Share my Instagram</span>
            <span
              className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                shareIg ? "bg-primary-500 justify-end" : "bg-neutral-300 dark:bg-neutral-600 justify-start"
              }`}
            >
              <span className="h-4 w-4 rounded-full bg-white shadow-sm" />
            </span>
          </button>
          {shareIg && (
            <div className="mt-2.5 flex items-center gap-1.5 pl-7">
              <span className="text-neutral-400 text-sm">@</span>
              <input
                type="text"
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value.replace(/^@/, ""))}
                maxLength={100}
                placeholder="yourhandle"
                className="input input-sm flex-1"
              />
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-4 flex items-center gap-2 sticky bottom-0 bg-white dark:bg-neutral-900">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Cancel
        </button>
        <button
          type="button"
          disabled={apply.isPending}
          onClick={handleSubmit}
          className="btn btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {apply.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send reach-out
        </button>
      </div>
    </SheetModal>
  );
}
